import React from 'react';

/*
 * The phone navigation bar.
 *
 * Six places under the thumb: Home, My ID, Comps, Events, Profile, More. Six
 * is the most that fits — at 390px that is 65px each, which holds a glyph and
 * a short word. Seven was tried on paper and the last two fell off the right
 * edge behind a sideways scroll, which people do not find.
 *
 * Everything occasional lives behind More: results, the club overview, help,
 * logging out, and an admin's half-dozen extras. That keeps a member's bar and
 * an admin's bar the same shape, so the app does not rearrange itself
 * depending on who is holding it.
 *
 * The bar is phone-only. On anything wider the existing top nav has room for
 * all of this and two bars would just disagree with each other.
 */

const ITEMS = [
  { id: 'home', label: 'Home', icon: 'home' },
  { id: 'memberId', label: 'My ID', icon: 'id-card' },
  { id: 'competitions', label: 'Comps', icon: 'trophy' },
  { id: 'events', label: 'Events', icon: 'calendar' },
  { id: 'profile', label: 'Profile', icon: 'user' },
];

/** Which bar item should look current, given the view the app is showing. */
export function activeNavItem(view) {
  if (view === 'home') return 'home';
  if (view === 'memberId') return 'memberId';
  if (view === 'competitions' || view === 'competition' || view === 'newComp') return 'competitions';
  if (view === 'events') return 'events';
  if (view === 'playerProfile') return 'profile';
  return null; // a screen reached through More: nothing in the bar owns it
}

function Sheet({ children, onClose, title }) {
  const closeRef = React.useRef(null);
  const openerRef = React.useRef(null);
  React.useEffect(() => {
    openerRef.current = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
    };
  }, [onClose]);

  return (
    <div className="mnav-sheet-back" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="mnav-sheet" onClick={e => e.stopPropagation()}>
        <div className="mnav-sheet-grip" aria-hidden="true" />
        {children}
        <button type="button" ref={closeRef} className="mnav-sheet-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function Tile({ icon, label, onClick, badge, admin }) {
  return (
    <button type="button" className={`mnav-tile${admin ? ' mnav-tile-admin' : ''}`} onClick={onClick}>
      <i className={`fa fa-${icon} mnav-tile-glyph`} aria-hidden="true" />
      <span>{label}</span>
      {badge ? <span className="mnav-badge">{badge}</span> : null}
    </button>
  );
}

/**
 * @param go        (id) => void — one of the five bar destinations
 * @param more      the extra destinations, [{ id, label, glyph, onClick, badge, admin }]
 * @param view      the view the app is currently showing
 */
function MobileNav({ go, more = [], view }) {
  const [sheet, setSheet] = React.useState(false);
  const active = activeNavItem(view);
  const memberItems = more.filter(m => !m.admin);
  const adminItems = more.filter(m => m.admin);
  // A count on More, so an admin knows something is waiting without opening it.
  const pending = more.reduce((n, m) => n + (Number(m.badge) || 0), 0);

  const pick = item => { setSheet(false); item.onClick(); };

  return (
    <>
      <nav className="mnav" aria-label="Main">
        {ITEMS.map(it => (
          <button
            type="button"
            key={it.id}
            className={`mnav-btn${active === it.id ? ' mnav-btn-on' : ''}`}
            aria-current={active === it.id ? 'page' : undefined}
            onClick={() => go(it.id)}>
            <i className={`fa fa-${it.icon} mnav-glyph`} aria-hidden="true" />
            <span className="mnav-label">{it.label}</span>
          </button>
        ))}
        <button
          type="button"
          className={`mnav-btn${sheet ? ' mnav-btn-on' : ''}`}
          aria-expanded={sheet}
          onClick={() => setSheet(v => !v)}>
          <i className="fa fa-ellipsis-h mnav-glyph" aria-hidden="true" />
          <span className="mnav-label">More</span>
          {pending > 0 && <span className="mnav-dot" aria-label={`${pending} waiting`} />}
        </button>
      </nav>

      {sheet && (
        <Sheet title="More" onClose={() => setSheet(false)}>
          {memberItems.length > 0 && (
            <>
              <h4 className="mnav-sheet-label">More</h4>
              <div className="mnav-grid">
                {memberItems.map(m => <Tile key={m.id} {...m} onClick={() => pick(m)} />)}
              </div>
            </>
          )}
          {adminItems.length > 0 && (
            <>
              <h4 className="mnav-sheet-label">Admin</h4>
              <div className="mnav-grid">
                {adminItems.map(m => <Tile key={m.id} {...m} onClick={() => pick(m)} />)}
              </div>
            </>
          )}
        </Sheet>
      )}
    </>
  );
}

export default MobileNav;
